'use client';

import AppLayout from '@/components/AppLayout';
import { Settings, User, Bell, Shield, Palette, Database } from 'lucide-react';

interface SettingSection {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  settings: { label: string; description: string; type: 'toggle' | 'select' | 'text'; value: string | boolean }[];
}

const settingSections: SettingSection[] = [
  {
    id: 'profile',
    title: 'Profile',
    description: 'Manage your personal information',
    icon: User,
    settings: [
      { label: 'Display Name', description: 'Your name shown across the app', type: 'text', value: 'Marcus Webb' },
      { label: 'Email', description: 'Your login email address', type: 'text', value: 'marcus.webb@guitarvault.app' },
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Control when and how you receive alerts',
    icon: Bell,
    settings: [
      { label: 'Maintenance Reminders', description: 'Get notified when maintenance is due', type: 'toggle', value: true },
      { label: 'String Change Alerts', description: 'Alerts when strings need changing', type: 'toggle', value: true },
      { label: 'Loan Reminders', description: 'Reminders for guitars on loan', type: 'toggle', value: false },
    ],
  },
  {
    id: 'appearance',
    title: 'Appearance',
    description: 'Customize the look of GuitarVault',
    icon: Palette,
    settings: [
      { label: 'Theme', description: 'Choose your preferred color theme', type: 'select', value: 'Light' },
      { label: 'Compact Mode', description: 'Show more content with less spacing', type: 'toggle', value: false },
    ],
  },
  {
    id: 'security',
    title: 'Security',
    description: 'Keep your account secure',
    icon: Shield,
    settings: [
      { label: 'Two-Factor Authentication', description: 'Add an extra layer of security', type: 'toggle', value: false },
      { label: 'Session Timeout', description: 'Auto sign-out after inactivity', type: 'select', value: '30 minutes' },
    ],
  },
  {
    id: 'data',
    title: 'Data & Privacy',
    description: 'Manage your data and export options',
    icon: Database,
    settings: [
      { label: 'Export Collection', description: 'Download your guitar data as CSV', type: 'select', value: 'CSV' },
      { label: 'Analytics', description: 'Help improve GuitarVault with usage data', type: 'toggle', value: true },
    ],
  },
];

export default function SettingsPage() {
  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your account preferences and app configuration</p>
        </div>

        {/* Settings Sections */}
        <div className="space-y-4">
          {settingSections.map((section) => {
            const SectionIcon = section.icon;
            return (
              <div key={section.id} className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <SectionIcon size={15} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">{section.title}</h2>
                    <p className="text-xs text-muted-foreground">{section.description}</p>
                  </div>
                </div>
                <div className="divide-y divide-border">
                  {section.settings.map((setting, idx) => (
                    <div key={idx} className="px-5 py-4 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">{setting.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{setting.description}</p>
                      </div>
                      <div className="shrink-0">
                        {setting.type === 'toggle' && (
                          <button
                            className={`relative w-10 h-5 rounded-full transition-colors ${
                              setting.value ? 'bg-primary' : 'bg-muted-foreground/30'
                            }`}
                            aria-label={`Toggle ${setting.label}`}
                          >
                            <span
                              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                                setting.value ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        )}
                        {setting.type === 'text' && (
                          <input
                            type="text"
                            defaultValue={setting.value as string}
                            className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 w-48"
                          />
                        )}
                        {setting.type === 'select' && (
                          <select
                            defaultValue={setting.value as string}
                            className="text-sm border border-border rounded-lg px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                          >
                            <option>{setting.value as string}</option>
                          </select>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
